import { useState } from 'react';
import { useCustomerGroupsPaged } from '../hooks/useCustomerGroups';
import { CustomerGroupsTable } from '../components/CustomerGroupsTable';
import { CreateGroupsDialog } from '../components/CreateGroupDialog';
import { CustomerGroupCriteriaDialog } from '../components/CustomerGroupCriteriaDialog';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { TablePagination } from '@/shared/components/TablePagination';
import { useDebounce } from '@/shared/hooks/useDebounce';

export const CustomersGroupManagement = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isFetching, error } = useCustomerGroupsPaged({
    pageNumber,
    pageSize,
    search: debouncedSearch.trim() || undefined,
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageNumber(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  if (isLoading && !data) {
    return <LoadingAnimation fullScreen />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load customer groups</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Customer Groups
          </h1>
          <p className="text-gray-500 mt-1">
            Manage customer groups and their default settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CustomerGroupCriteriaDialog />
          <CreateGroupsDialog />
        </div>
      </div>

      <CustomerGroupsTable
        groups={data?.items ?? []}
        searchValue={search}
        onSearchChange={handleSearchChange}
      />

      <TablePagination
        pageNumber={data?.pageNumber ?? pageNumber}
        pageSize={data?.pageSize ?? pageSize}
        totalCount={data?.totalCount ?? 0}
        totalPages={data?.totalPages ?? 0}
        onPageChange={setPageNumber}
        onPageSizeChange={handlePageSizeChange}
        isFetching={isFetching}
      />
    </div>
  );
};
